/**
 * Runs the atmosphere LUT passes and owns the sky dome. Transmittance and multi-scattering are computed once;
 * the sun and moon sky-view LUTs are recomputed when a light moved > 0.1° and at most every 250 ms
 * (Review Focus 5). A 32×16 read-back of the sun sky-view feeds the scene's LightProbe (SH9).
 */
import * as THREE from 'three';
import { EYE } from '../composition';
import type { Vec3 } from '../frame';
import { ATMOSPHERE } from './atmosphere';
import {
  FULLSCREEN_VERT,
  MULTISCATTER_FRAG,
  SKYVIEW_FRAG,
  SKY_DOME_FRAG,
  SKY_DOME_VERT,
  TRANSMITTANCE_FRAG,
} from './atmosphereShaders';
import type { CloudLayer } from './cloudParams';
import { projectSH9 } from './sh';
import { dirToSkyViewUv } from './skyViewMapping';

export const PLATFORM_ALTITUDE_M = 80;
export const CAMERA_RADIUS_M = ATMOSPHERE.groundRadiusM + PLATFORM_ALTITUDE_M + EYE[1];
const MIN_UPDATE_MS = 250;
const MIN_MOVE_COS = Math.cos((0.1 * Math.PI) / 180);
const SH_W = 32;
const SH_H = 16;

export interface SkyLights {
  sunDir: Vec3;
  moonDir: Vec3;
  sunIlluminance: number;
  moonIlluminance: number;
  townGlow: number;
  clouds: CloudLayer[];
  cloudOffsets: Array<[number, number]>;
  cloudDetail: number;
}

function target(w: number, h: number, type: THREE.TextureDataType): THREE.WebGLRenderTarget {
  return new THREE.WebGLRenderTarget(w, h, {
    type,
    format: THREE.RGBAFormat,
    minFilter: THREE.LinearFilter,
    magFilter: THREE.LinearFilter,
    wrapS: THREE.ClampToEdgeWrapping,
    wrapT: THREE.ClampToEdgeWrapping,
    depthBuffer: false,
    generateMipmaps: false,
  });
}

export class SkyRenderer {
  readonly dome: THREE.Mesh;
  readonly probe = new THREE.LightProbe();
  readonly transmittance: THREE.WebGLRenderTarget;
  readonly multiscatter: THREE.WebGLRenderTarget;
  readonly skySun: THREE.WebGLRenderTarget;
  readonly skyMoon: THREE.WebGLRenderTarget;
  private readonly shTarget: THREE.WebGLRenderTarget;
  private readonly quadScene = new THREE.Scene();
  private readonly quadCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private readonly quad: THREE.Mesh;
  private readonly skyViewMat: THREE.ShaderMaterial;
  private readonly domeUniforms: Record<string, THREE.IUniform>;
  private lastUpdateMs = -Infinity;
  private lastSun = new THREE.Vector3(0, -1, 0);
  private lastMoon = new THREE.Vector3(0, -1, 0);
  private initialised = false;
  private shPending = false;
  /** False when float render targets are missing: the caller should drop to `low`. */
  readonly floatTargets: boolean;

  constructor(private readonly renderer: THREE.WebGLRenderer) {
    const gl = renderer.getContext();
    this.floatTargets = !!(gl.getExtension('EXT_color_buffer_half_float') || gl.getExtension('EXT_color_buffer_float'));
    const type = this.floatTargets ? THREE.HalfFloatType : THREE.UnsignedByteType;
    if (!this.floatTargets) console.warn('[sky] no float render targets: 8-bit fallback, quality low');
    this.transmittance = target(256, 64, type);
    this.multiscatter = target(32, 32, type);
    this.skySun = target(192, 108, type);
    this.skyMoon = target(192, 108, type);
    // u = 0 is the camera azimuth: wrap horizontally so there is no seam in the middle of the frame.
    for (const t of [this.skySun, this.skyMoon]) t.texture.wrapS = THREE.RepeatWrapping;
    this.shTarget = target(SH_W, SH_H, THREE.HalfFloatType);

    this.quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2));
    this.quad.frustumCulled = false;
    this.quadScene.add(this.quad);

    this.skyViewMat = new THREE.ShaderMaterial({
      vertexShader: FULLSCREEN_VERT,
      fragmentShader: SKYVIEW_FRAG,
      uniforms: {
        uTransmittance: { value: this.transmittance.texture },
        uMultiscatter: { value: this.multiscatter.texture },
        uLightDir: { value: new THREE.Vector3(0, 1, 0) },
        uCamR: { value: CAMERA_RADIUS_M },
      },
      depthTest: false,
      depthWrite: false,
    });

    this.domeUniforms = {
      uTransmittance: { value: this.transmittance.texture },
      uSkySun: { value: this.skySun.texture },
      uSkyMoon: { value: this.skyMoon.texture },
      uSunDir: { value: new THREE.Vector3(0, 1, 0) },
      uSunIlluminance: { value: 20 },
      uMoonIlluminance: { value: 0 },
      uCamR: { value: CAMERA_RADIUS_M },
      uAirglow: { value: new THREE.Vector3(0.0008, 0.001, 0.0006) },
      uTownGlowColor: { value: new THREE.Vector3(1.0, 0.55, 0.25) },
      uTownGlowDir: { value: new THREE.Vector3(0, 0, -1) },
      uTownGlow: { value: 0 },
      uMoonDir: { value: new THREE.Vector3(0, -1, 0) },
      uCloudA: { value: [new THREE.Vector4(), new THREE.Vector4(), new THREE.Vector4()] },
      uCloudOffset: { value: [new THREE.Vector2(), new THREE.Vector2(), new THREE.Vector2()] },
      uCloudDetail: { value: 4 },
    };
    this.dome = new THREE.Mesh(
      new THREE.SphereGeometry(1, 48, 24),
      new THREE.ShaderMaterial({
        vertexShader: SKY_DOME_VERT,
        fragmentShader: SKY_DOME_FRAG,
        uniforms: this.domeUniforms,
        side: THREE.BackSide,
        depthWrite: false,
      }),
    );
    this.dome.name = 'sky';
    this.dome.frustumCulled = false;
    this.dome.renderOrder = -100;
  }

  private pass(material: THREE.Material, out: THREE.WebGLRenderTarget): void {
    this.quad.material = material;
    const prev = this.renderer.getRenderTarget();
    this.renderer.setRenderTarget(out);
    this.renderer.render(this.quadScene, this.quadCam);
    this.renderer.setRenderTarget(prev);
  }

  private init(): void {
    this.pass(new THREE.ShaderMaterial({ vertexShader: FULLSCREEN_VERT, fragmentShader: TRANSMITTANCE_FRAG, depthTest: false, depthWrite: false }), this.transmittance);
    this.pass(
      new THREE.ShaderMaterial({
        vertexShader: FULLSCREEN_VERT,
        fragmentShader: MULTISCATTER_FRAG,
        uniforms: { uTransmittance: { value: this.transmittance.texture } },
        depthTest: false,
        depthWrite: false,
      }),
      this.multiscatter,
    );
    this.initialised = true;
  }

  /** Keeps the dome on the camera and refreshes LUTs when needed. `force` bypasses the rate limit (time jumps). */
  update(lights: SkyLights, camera: THREE.Camera, nowMs: number, force = false): void {
    if (!this.initialised) this.init();
    this.dome.position.copy(camera.position);
    const sun = new THREE.Vector3(...lights.sunDir);
    const moon = new THREE.Vector3(...lights.moonDir);
    const moved = sun.dot(this.lastSun) < MIN_MOVE_COS || moon.dot(this.lastMoon) < MIN_MOVE_COS;
    if ((moved && nowMs - this.lastUpdateMs >= MIN_UPDATE_MS) || force || this.lastUpdateMs === -Infinity) {
      this.skyViewMat.uniforms.uLightDir!.value.copy(sun);
      this.pass(this.skyViewMat, this.skySun);
      if (lights.moonIlluminance > 0) {
        this.skyViewMat.uniforms.uLightDir!.value.copy(moon);
        this.pass(this.skyViewMat, this.skyMoon);
      }
      this.lastSun.copy(sun);
      this.lastMoon.copy(moon);
      this.lastUpdateMs = nowMs;
      this.refreshProbe(lights);
    }
    this.domeUniforms.uSunDir!.value.copy(sun);
    this.domeUniforms.uSunIlluminance!.value = lights.sunIlluminance;
    this.domeUniforms.uMoonIlluminance!.value = lights.moonIlluminance;
    this.domeUniforms.uTownGlow!.value = lights.townGlow;
    this.domeUniforms.uMoonDir!.value.copy(moon);
    const a = this.domeUniforms.uCloudA!.value as THREE.Vector4[];
    const off = this.domeUniforms.uCloudOffset!.value as THREE.Vector2[];
    lights.clouds.forEach((l, i) => {
      a[i]!.set(l.threshold, l.softness, l.scaleM, l.altitudeM);
      off[i]!.set(...(lights.cloudOffsets[i] ?? [0, 0]));
    });
    this.domeUniforms.uCloudDetail!.value = lights.cloudDetail;
  }

  /** Down-samples the sky into SH_W×SH_H in sky-view space and projects it onto SH9 for the LightProbe. */
  private refreshProbe(lights: SkyLights): void {
    if (this.shPending) return;
    this.shPending = true;
    const copy = new THREE.ShaderMaterial({
      vertexShader: FULLSCREEN_VERT,
      fragmentShader: /* glsl */ `
        uniform sampler2D uSun; uniform sampler2D uMoon; uniform float uSunI; uniform float uMoonI; varying vec2 vUv;
        void main() { gl_FragColor = vec4(texture2D(uSun, vUv).rgb * uSunI + texture2D(uMoon, vUv).rgb * uMoonI, 1.0); }`,
      uniforms: {
        uSun: { value: this.skySun.texture },
        uMoon: { value: this.skyMoon.texture },
        uSunI: { value: lights.sunIlluminance },
        uMoonI: { value: lights.moonIlluminance },
      },
      depthTest: false,
      depthWrite: false,
    });
    this.pass(copy, this.shTarget);
    copy.dispose();
    const buf = new Uint16Array(SH_W * SH_H * 4);
    this.renderer
      .readRenderTargetPixelsAsync(this.shTarget, 0, 0, SH_W, SH_H, buf)
      .then(() => {
        const px = (u: number, v: number): [number, number, number] => {
          const x = Math.min(SH_W - 1, Math.floor(u * SH_W));
          const y = Math.min(SH_H - 1, Math.floor(v * SH_H));
          const i = (y * SH_W + x) * 4;
          const h = THREE.DataUtils.fromHalfFloat;
          return [h(buf[i]!), h(buf[i + 1]!), h(buf[i + 2]!)];
        };
        const coeffs = projectSH9((d) => {
          const [u, v] = dirToSkyViewUv(d, CAMERA_RADIUS_M);
          // below the local horizon the "sky" is the ground: approximate with a dim, warm bounce
          const c = px(u, v);
          return d[1] < -0.05 ? [c[0] * 0.25, c[1] * 0.22, c[2] * 0.18] : c;
        });
        this.probe.sh.fromArray(coeffs.flat());
      })
      .catch(() => undefined)
      .finally(() => (this.shPending = false));
  }
}
